import React, { useState, useEffect } from 'react';
import { getPointsLeaderboard } from '../apiClient';
import { useJWTAuth } from '../JWTAuthContext';

interface LeaderboardUser {
  rank: number;
  user_id: number;
  user_name: string;
  total_points: number;
  current_week_points?: number;
  monthly_points?: number;
}

interface LeaderboardData {
  leaderboard: LeaderboardUser[];
  period: string;
  limit: number;
  total_users: number;
  generated_at: string;
}

const PointsLeaderboard: React.FC = () => {
  const { user } = useJWTAuth();
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('all_time');
  const [userRank, setUserRank] = useState<LeaderboardUser | null>(null);

  const fetchLeaderboard = async (period: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getPointsLeaderboard({ period, limit: 5 });
      if (response && response.status === 'success' && response.result) {
        setLeaderboardData(response.result);
        
        // Find current user in leaderboard
        if (user && user.id) {
          const currentUserEntry = response.result.leaderboard.find(
            (entry: LeaderboardUser) => entry.user_id === parseInt(user.id)
          );
          setUserRank(currentUserEntry || null);
        }
      } else {
        setError('Failed to load leaderboard');
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
      setError('Error loading leaderboard data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard(selectedPeriod);
  }, [selectedPeriod, user]);

  const getPeriodLabel = (period: string) => {
    switch (period) {
      case 'weekly': return 'This Week';
      case 'monthly': return 'This Month';
      case 'all_time': return 'All Time';
      default: return period;
    }
  };

  const getPointsForPeriod = (userEntry: LeaderboardUser, period: string) => {
    switch (period) {
      case 'weekly': return userEntry.current_week_points || userEntry.total_points;
      case 'monthly': return userEntry.monthly_points || userEntry.total_points;
      case 'all_time': return userEntry.total_points;
      default: return userEntry.total_points;
    }
  };

  const getRankBadgeClass = (rank: number) => {
    switch (rank) {
      case 1: return 'bg-warning text-dark'; // Gold
      case 2: return 'bg-secondary text-white'; // Silver
      case 3: return 'bg-dark text-white'; // Bronze
      default: return 'bg-primary text-white';
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1: return 'fas fa-crown';
      case 2: return 'fas fa-medal';
      case 3: return 'fas fa-award';
      default: return 'fas fa-hashtag';
    }
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body d-flex align-items-center justify-content-center" style={{ minHeight: '300px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="alert alert-warning" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="row align-items-center">
          <div className="col">
            <h5 className="card-title mb-0">
              <i className="fas fa-trophy me-2 text-warning"></i>
              Points Leaderboard
            </h5>
          </div>
          <div className="col-auto">
            <div className="btn-group btn-group-sm" role="group">
              <input
                type="radio"
                className="btn-check"
                name="period"
                id="weekly"
                value="weekly"
                checked={selectedPeriod === 'weekly'}
                onChange={(e) => setSelectedPeriod(e.target.value)}
              />
              <label className="btn btn-outline-primary" htmlFor="weekly">
                Weekly
              </label>

              <input
                type="radio"
                className="btn-check"
                name="period"
                id="monthly"
                value="monthly"
                checked={selectedPeriod === 'monthly'}
                onChange={(e) => setSelectedPeriod(e.target.value)}
              />
              <label className="btn btn-outline-primary" htmlFor="monthly">
                Monthly
              </label>

              <input
                type="radio"
                className="btn-check"
                name="period"
                id="all_time"
                value="all_time"
                checked={selectedPeriod === 'all_time'}
                onChange={(e) => setSelectedPeriod(e.target.value)}
              />
              <label className="btn btn-outline-primary" htmlFor="all_time">
                All Time
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="card-body p-0">
        {/* Current User Rank Display */}
        {userRank && (
          <div className="alert alert-info mb-0 border-0 rounded-0">
            <div className="row align-items-center">
              <div className="col-auto">
                <span className={`badge ${getRankBadgeClass(userRank.rank)} me-2`}>
                  <i className={getRankIcon(userRank.rank)} me-1></i>
                  #{userRank.rank}
                </span>
              </div>
              <div className="col">
                <strong>Your Position</strong>
              </div>
              <div className="col-auto">
                <strong>{getPointsForPeriod(userRank, selectedPeriod).toLocaleString()} points</strong>
              </div>
            </div>
          </div>
        )}

        {leaderboardData && leaderboardData.leaderboard.length > 0 ? (
          <div className="list-group list-group-flush">
            {leaderboardData.leaderboard.map((entry) => (
              <div 
                key={entry.user_id} 
                className={`list-group-item ${entry.user_id === parseInt(user?.id || '0') ? 'bg-light border-primary' : ''}`}
              >
                <div className="row align-items-center">
                  <div className="col-auto">
                    <span className={`badge ${getRankBadgeClass(entry.rank)}`}>
                      <i className={getRankIcon(entry.rank)} me-1></i>
                      #{entry.rank}
                    </span>
                  </div>
                  
                  <div className="col">
                    <div className="fw-bold">
                      {entry.user_name}
                      {entry.user_id === parseInt(user?.id || '0') && (
                        <span className="badge bg-primary ms-2">You</span>
                      )}
                    </div>
                    <small className="text-muted">
                      User ID: {entry.user_id}
                    </small>
                  </div>
                  
                  <div className="col-auto text-end">
                    <div className="fw-bold text-primary h5 mb-0">
                      {getPointsForPeriod(entry, selectedPeriod).toLocaleString()}
                    </div>
                    <small className="text-muted">points</small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-5">
            <i className="fas fa-trophy text-muted mb-3" style={{ fontSize: '3rem' }}></i>
            <h5 className="text-muted">No Rankings Available</h5>
            <p className="text-muted">
              Be the first to earn points and appear on the leaderboard!
            </p>
          </div>
        )}
      </div>

      {leaderboardData && (
        <div className="card-footer text-center text-muted">
          <small>
            Showing top {leaderboardData.total_users} users • {getPeriodLabel(selectedPeriod)} leaderboard
            <br />
            Last updated: {new Date(leaderboardData.generated_at).toLocaleString()}
          </small>
        </div>
      )}
    </div>
  );
};

export default PointsLeaderboard;